
import { useState } from "react";
import { Radio, MessageCircle, Clock, CheckCircle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { CastResponseModal } from "./CastResponseModal";
import { TagButton } from "./TagButton";
import { useCastResponses } from "@/hooks/useCastResponses";
import { formatDistanceToNow } from "date-fns";
import { formatRelativeSafe } from "@/utils/date";
import type { Cast } from "@/hooks/useCasts";

interface ISOCastsTabProps {
  casts: Cast[];
  onToggleSolved?: (castId: string) => void;
}

export const ISOCastsTab = ({ casts, onToggleSolved }: ISOCastsTabProps) => {
  const [selectedCast, setSelectedCast] = useState<Cast | null>(null);

  console.log('🔍 ISOCastsTab - onToggleSolved prop:', !!onToggleSolved);
  console.log('🔍 ISOCastsTab - casts:', casts.length);

  if (casts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Radio className="mx-auto mb-4 text-gray-300" size={48} />
        <p className="text-lg font-medium">No ISO Casts yet</p>
        <p className="text-sm mt-2">Your ISO requests will appear here</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-ink-primary flex items-center gap-2">
          <Radio className="text-iso-solid" size={20} />
          My ISO Casts ({casts.length})
        </h3>
        
        <div className="space-y-2">
          {casts.map((cast) => (
            <ISOCastCard
              key={cast.id}
              cast={cast}
              onToggleSolved={onToggleSolved}
              onViewResponses={() => setSelectedCast(cast)}
            />
          ))}
        </div>
      </div>

      {selectedCast && (
        <CastResponseModal
          cast={selectedCast}
          onClose={() => setSelectedCast(null)}
        />
      )}
    </>
  );
};

interface ISOCastCardProps {
  cast: Cast;
  onToggleSolved?: (castId: string) => void;
  onViewResponses: () => void;
}

const ISOCastCard = ({ cast, onToggleSolved, onViewResponses }: ISOCastCardProps) => {
  const { responses, loading } = useCastResponses(cast.id);

  const handleToggleSolved = async () => {
    console.log('🔘 Mark Solved button clicked for cast:', cast.id);
    console.log('🔘 Cast solved status:', cast.solved);
    console.log('🔘 onToggleSolved function available:', !!onToggleSolved);
    
    if (onToggleSolved) {
      try {
        console.log('🔄 Calling onToggleSolved...');
        await onToggleSolved(cast.id);
        console.log('✅ onToggleSolved completed successfully');
      } catch (error) {
        console.error('❌ Error calling onToggleSolved:', error);
      }
    } else {
      console.error('❌ onToggleSolved function not provided');
    }
  };

  return (
    <div className={`rounded-lg shadow-sm border overflow-hidden ${
      cast.solved 
        ? 'bg-gray-50 border-gray-200' 
        : cast.sentiment === 'help' 
          ? 'bg-red-50 border-red-200' 
          : 'bg-white border-gray-200'
    }`}>
      {/* Compact Header */}
      <div className={`px-3 py-2 border-b ${
        cast.solved 
          ? 'bg-gray-100 border-gray-200' 
          : cast.sentiment === 'help'
            ? 'bg-red-100 border-red-200'
            : 'bg-iso-soft-bg border-iso-soft-border'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className={
              cast.solved 
                ? "text-gray-600" 
                : cast.sentiment === 'help' 
                  ? "text-red-600" 
                  : "text-iso-solid"
            } size={14} />
            <span className={`text-sm font-medium ${
              cast.solved 
                ? "text-gray-700" 
                : cast.sentiment === 'help' 
                  ? "text-red-800" 
                  : "text-[#3E1C8A]"
            }`}>
              ISO Cast
            </span>
            {cast.solved && (
              <Badge className="bg-gray-200 text-gray-700 border-gray-300 text-xs px-1.5 py-0.5">
                <CheckCircle size={10} className="mr-1" />
                Solved
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1 text-xs ${
              cast.solved 
                ? "text-gray-600" 
                : cast.sentiment === 'help' 
                  ? "text-red-600" 
                  : "text-iso-solid"
            }`}>
              <Clock size={10} />
              <span>
                {formatRelativeSafe(cast.created_at as any)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Content */}
      <div className="p-3">
        {/* ISO Request - Compact */}
        <div className={`rounded-md p-2 border-l-2 mb-2 ${
          cast.solved 
            ? 'bg-gray-100 border-gray-400' 
            : cast.sentiment === 'help'
              ? 'bg-red-100 border-red-500'
              : 'bg-iso-soft-bg border-iso-soft-border'
        }`}>
          <h4 className="font-medium text-gray-800 text-xs mb-1">Looking for:</h4>
          <p className={`text-gray-700 text-sm ${cast.solved ? 'line-through opacity-75' : ''}`}>
            {cast.iso_request}
          </p>
        </div>

        {/* Compact Actions Row */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-3 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <MessageCircle size={12} />
              <span>{loading ? '...' : responses.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin size={10} />
              <span>{cast.location_radius_km}km</span>
            </div>
            <TagButton
              targetUserId={(cast as any).user_id || ''}
              targetUserName={cast.user?.name || 'Unknown User'}
              contentType="cast"
              contentId={cast.id}
            />
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewResponses}
              className="text-xs h-6 px-2 min-w-0"
            >
              <span className="hidden sm:inline">Responses</span>
              <span className="sm:hidden">
                <MessageCircle size={12} />
              </span>
            </Button>
            {!cast.solved && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleSolved}
                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 text-xs h-6 px-2 min-w-0 flex-shrink-0"
              >
                <span className="hidden sm:inline">Solve</span>
                <span className="sm:hidden">
                  <CheckCircle size={12} />
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
